import { SquaresFour, Table as TableIcon } from "@phosphor-icons/react";
import { Banner, Tab, TabList, TabPanel, TabProvider } from "@replicate/ui";
import { useEffect, useState, type ReactNode } from "react";

import { useQueryParam } from "../../hooks";
import type { BillingConfig, BillingConfigTiers } from "../../types";
import { GridView } from "./grid-view";
import { SimpleView } from "./simple-view";
import { TableView } from "./table-view";

interface TabButtonProps {
  icon: ReactNode;
  children: ReactNode;
}

function TabButton({ icon, children }: TabButtonProps) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 text-r8-gray-11 hover:text-r8-gray-12">
      {icon}
      <span>{children}</span>
    </div>
  );
}

interface ModelBillingProps {
  modelName: string;
  billingConfig: BillingConfig;
  showTable?: boolean;
}

function ModelBillingLayout({ children }: { children: ReactNode }) {
  return (
    <div className="space-y-8">
      <h4 className="flex-1 border-b pb-2">Pricing</h4>
      {children}
    </div>
  );
}

export default function ModelBilling({
  modelName,
  billingConfig,
  showTable,
}: ModelBillingProps): ReactNode {
  const tiers = billingConfig.current_tiers ?? [];

  const simpleView = tiers.length < 2;

  if (simpleView) {
    return (
      <ModelBillingLayout>
        <Simple
          description={billingConfig.current_description}
          modelName={modelName}
          tiers={tiers}
        />
      </ModelBillingLayout>
    );
  }

  if (
    showTable &&
    tiers.length > 4 &&
    tiers.every(
      (tier) =>
        tier.criteria.length > 0 &&
        // The table view only currently support equality criteria, so we can't
        // show it if there are ranges.
        tier.criteria.every((criterion) => criterion.type === "equals")
    )
  ) {
    return (
      // Tabbed handles its own layout
      <Tabbed
        description={billingConfig.current_description}
        modelName={modelName}
        tiers={tiers}
      />
    );
  }

  return (
    <ModelBillingLayout>
      <Grid
        description={billingConfig.current_description}
        modelName={modelName}
        tiers={tiers}
      />
    </ModelBillingLayout>
  );
}

function Simple({
  description,
  modelName,
  tiers,
}: {
  description: string | null;
  modelName: string;
  tiers: BillingConfigTiers;
}): ReactNode {
  return (
    <SimpleView modelName={modelName} tiers={tiers} description={description} />
  );
}

function Grid({
  description,
  modelName,
  tiers,
}: {
  description: string | null;
  modelName: string;
  tiers: BillingConfigTiers;
}): ReactNode {
  return (
    <GridView modelName={modelName} tiers={tiers} description={description} />
  );
}

function Tabbed({
  description,
  modelName,
  tiers,
}: {
  description: string | null;
  modelName: string;
  tiers: BillingConfigTiers;
}): ReactNode {
  const defaultView = "table";
  const [activeId, setActiveId] = useQueryParam("view", defaultView);

  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 639px)");

    const handleMediaQueryChange = (e: MediaQueryListEvent) => {
      if (e.matches && activeId !== "grid") {
        setActiveId("grid");
        setShowBanner(true);
      }
    };

    mediaQuery.addEventListener("change", handleMediaQueryChange);

    return () =>
      mediaQuery.removeEventListener("change", handleMediaQueryChange);
  }, [setActiveId, activeId]);

  return (
    <TabProvider
      defaultActiveId={defaultView}
      activeId={activeId}
      setActiveId={(id) => {
        if (id) {
          setActiveId(id);
          setShowBanner(false);
        }
      }}
      selectedId={activeId}
    >
      <TabList className="mb-8">
        <h4 className="flex-1">Pricing</h4>
        <Tab id="grid" className="hidden sm:block">
          <TabButton icon={<SquaresFour size={24} />}>Grid View</TabButton>
        </Tab>
        <Tab id="table" className="hidden sm:block">
          <TabButton icon={<TableIcon size={24} />}>Table View</TabButton>
        </Tab>
      </TabList>
      {showBanner && (
        <Banner
          severity="info"
          description="Switched to grid view for a better mobile experience"
          onDismiss={() => setShowBanner(false)}
          dismissible
          condensed
        />
      )}
      <TabPanel>
        <GridView
          modelName={modelName}
          tiers={tiers}
          description={description}
        />
      </TabPanel>
      <TabPanel>
        <TableView
          modelName={modelName}
          tiers={tiers}
          description={description}
        />
      </TabPanel>
    </TabProvider>
  );
}
